import fetch from 'isomorphic-fetch';
import { AppError } from './AppError';

export class HTTPClientConfig{
    baseURL = '';
    accessToken? = '';
    on401Callback?: () => Promise<any>;
    authPrefix? = '';
}

const createPromise = () => {
    let resolve: (result: any) => void;
    let reject: (error: any) => void;
    const promise: any = new Promise((_resolve, _reject) => {
        resolve = _resolve;
        reject = _reject;
    });
    promise.resolve = (result: any) => resolve(result);
    promise.reject = (error: any) => reject(error);
    return promise;
};

const getBodyAsBuffer = async (response: any) => {
    // If fetch response supports arrayBuffer (in browser), use that
    if ('arrayBuffer' in response) {
        return await response.arrayBuffer();
    }
    // Otherwise, fall back to streaming body into a buffer
    // (isomorphic fetch doesnt support this yet: https://github.com/bitinn/node-fetch/issues/51)
    return new Promise((resolve, reject) => {
        const chunks: any[] = [];
        response.body.on('error', (err: any) => reject(err));
        response.body.on('data', (chunk: any) => chunks.push(chunk));
        response.body.on('end', () => resolve(Buffer.concat(chunks)));
    });
};

export class HTTPClient {

    static inject = () => [HTTPClientConfig]
    constructor(private config?: HTTPClientConfig) {}

    private requestingCB = (_args: any) => {
        // no op
    };

    onRequesting = (cb: (args: any) => void) => {
        this.requestingCB = cb;
    }

    request = async ({ url, method = 'GET', data, headers = {}, options = {} }: any) => {
        if (!url) {
            throw new AppError({
                status: 500,
                message: 'url is required'
            });
        }

        const { baseURL = '', accessToken, on401Callback, authPrefix = 'JWT' } = this.config || {};

        const absoluteURL = url.startsWith('http') ? url : `${baseURL}${url}`;
        if (accessToken && !options.anonymous) {
            headers = {
                ...headers,
                authorization: `${authPrefix} ${accessToken}`
            };
        }

        let requestBody: any;
        if (data) {
            headers['content-type'] = headers['content-type'] || 'application/json';
            requestBody = JSON.stringify(data);
        }

        let response: any;

        try {
            const requestPromise = createPromise();

            // let listener know we are making a request without letting that cause us to fail
            if (!options.anonymous) {
                try {
                    this.requestingCB({ url: absoluteURL, method, headers, body: requestBody, requestPromise });
                } catch (e) {
                    // fail silently
                }
            }

            // use cache "no-store" to ensure the browser never saves an api response to disk
            // (chrome unexpectedly uses disk cache if the user hits the back button)
            const cache = (options.allowCaching ? 'default' : 'no-store');

            response = await fetch(absoluteURL, { method, headers, body: requestBody, cache });

            requestPromise.resolve({ status: response.status });
        } catch (e: any) {
            /**
             * Fetch does not throw unless there is a network error or misconfigured CORS headers
             * A common cause is API Gateway returning a 502 or 504 (which don't include CORS headers)
             * There is no obvious way to access the information on what the response looks like with
             * mode: 'cors' enabled, though it appears in the console
             * See https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#Checking_that_the_fetch_was_successful
             */
            throw new AppError({
                errorCode: 'fetch.failed',
                message: `Fetch failed with ${e.name} ${e.message}. ${method} ${absoluteURL}`,
                displayMessage: `We're sorry, we couldn't handle your request. Please try again.`
            });
        }

        if (response.status === 401 && on401Callback) {
            return await on401Callback();
        }

        if (!response.ok) {
            const { errorCode, message, details, displayMessage } = await this.getResponseJSON(response, { method, status: response.status, absoluteURL, ignoreBadJSON: true });
            throw new AppError({
                status: response.status,
                errorCode,
                message,
                displayMessage,
                details
            });
        }

        let body: any;
        if (options.asBuffer) {
            body = await getBodyAsBuffer(response);
        } else {
            body = response.status !== 204 ? await this.getResponseJSON(response, { method, status: response.status, absoluteURL }) : null;
        }

        return {
            status: response.status,
            url: response.url,
            data: body
        };
    };

    private getResponseJSON = async (response: any, { method, status, absoluteURL, ignoreBadJSON = false }: any) => {
        let body: any;
        try {
            body = await response.text();

            // occasionally we get a 200 with a null body, but we know the api never sends this response, so we are
            // setting an errorCode we can blocklist (assuming this indicates the request has been aborted)
            if (!body) {
                throw new AppError({
                    errorCode: 'fetch.missingBody',
                    message: `Received a 200 with no body likely because the request was aborted. ${method} ${absoluteURL}`
                });
            }

            return JSON.parse(body);

        } catch (e: any) {
            // detect if response.text() threw which should only be possible if the request
            // has been aborted (as the page is reloading)
            if (e.message === 'Failed to fetch'){
                throw new AppError({
                    errorCode: 'fetch.failed',
                    message: `Failed to get the body of the response likely because the request was aborted. ${method} ${absoluteURL}`
                });
            }

            if (ignoreBadJSON) {
                // doesn't matter if we can't get the body of an error resopnse (s3 returns html we can't parse)
                return { status, message: 'Could not parse the body of the response.' };
            }

            throw new AppError({
                status: 500,
                message: `Failed to parse body with error "${e.message}". ${status} ${method} ${absoluteURL} ${body || '[no body]'}`
            });
        }
    }

    delete = (url: string, headers?: any) => this.request({ url, method: 'DELETE', headers });
    patch = (url: string, data?: any, headers?: any) => this.request({ url, method: 'PATCH', data, headers });
    post = (url: string, data?: any, headers?: any) => this.request({ url, method: 'POST', data, headers });
    put = (url: string, data?: any, headers?: any) => this.request({ url, method: 'PUT', data, headers });
    get = (url: string, headers?: any, options?: any) => this.request({ url, method: 'GET', headers, options });
}
